import type { SvgIconComponent } from "@mui/icons-material";
import ContactsOutlinedIcon from "@mui/icons-material/ContactsOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import QrCode2OutlinedIcon from "@mui/icons-material/QrCode2Outlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import UnarchiveOutlinedIcon from "@mui/icons-material/UnarchiveOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Box, Typography } from "@mui/material";
import { forwardRef } from "react";
import { PermissionGate } from "@/components/PermissionGate";
import {
  CAMPAIGN_ACTION_OUT_OF_SCOPE_TOOLTIP,
  CAMPAIGN_ACTION_VIEW_ONLY_TOOLTIP,
} from "@/constants/campaignLifecycle";
import { usePermissions } from "@/hooks/usePermissions";
import type { CampaignAllowedActions } from "@/interfaces/campaing";
import type { ContactsMenuMode } from "@/interfaces/giftLinks";
import { contactsMenuLabel } from "./moreOptions-helper";

const ITEM_SX = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  px: "12px",
  py: "9px",
  cursor: "pointer",
  borderRadius: "7px",
  width: "100%",
  "&:hover": { bgcolor: "var(--color-wax)" },
} as const;

// Greyed, non-interactive: an action THIS campaign doesn't allow the caller (outside the groups they
// manage, or View Only). Shown rather than hidden so the user knows it exists. (Edit is *hidden* on
// a closed campaign, not greyed — see `canEdit` below.)
const DISABLED_ITEM_SX = {
  ...ITEM_SX,
  cursor: "default",
  opacity: 0.4,
  "&:hover": { bgcolor: "transparent" },
} as const;

const ICON_SX = { fontSize: 17, color: "var(--text-dust-ink)", flexShrink: 0 } as const;
const LABEL_SX = {
  fontFamily: "var(--font-lato)",
  fontSize: "13.5px",
  color: "var(--color-rodeo-black)",
} as const;

/** One menu row: a leading outlined icon + a Lato label, matching the prototype's `irMenuItem`. */
function MenuRow({
  icon: Icon,
  label,
  onClick,
  disabled = false,
  title,
}: {
  icon: SvgIconComponent;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <Box
      onClick={disabled ? undefined : onClick}
      sx={disabled ? DISABLED_ITEM_SX : ITEM_SX}
      aria-disabled={disabled || undefined}
      title={title}
    >
      <Icon sx={ICON_SX} />
      <Typography sx={LABEL_SX}>{label}</Typography>
    </Box>
  );
}

export interface MoreOptionsDropdownProps {
  onClose: () => void;
  onRename?: () => void;
  onClone?: () => void;
  onEdit?: () => void;
  onPreview?: () => void;
  onManageContacts?: () => void;
  onDelete?: () => void;
  onPause?: () => void;
  onResume?: () => void;
  onCancel?: () => void;
  onArchive?: () => void;
  /** Un-archive, back to the prior lifecycle status. */
  onRestore?: () => void;
  // Universal Link only: turn the shared /u/ link off/on, and copy it to share.
  onDisable?: () => void;
  onEnable?: () => void;
  onCopyLink?: () => void;
  /** Open the Redeem link & QR modal — gfg campaigns only, and only once Active (the card gates it). */
  onRedeemQr?: () => void;
  canPause?: boolean;
  canResume?: boolean;
  canDisable?: boolean;
  canEnable?: boolean;
  canCancel?: boolean;
  canArchive?: boolean;
  canRestore?: boolean;
  canDelete?: boolean;
  /**
   * When no terminal action (Cancel/Archive/Restore/Delete) applies, the greyed explanation of why —
   * e.g. a live campaign whose gift links are still redeemable. `null` when an action IS available.
   */
  blockedReason?: string | null;
  /**
   * Does the "Preview" item RENDER at all? Editors always preview (via the builder); a view-only
   * role can only preview when the campaign has a demo link to open, so `false` hides it for them
   * rather than leaving a menu entry that does nothing.
   */
  canPreview?: boolean;
  /**
   * Does the "Edit" item RENDER at all? `false` removes it from the DOM — a closed campaign has
   * nothing left to edit, so the option disappears instead of sitting there greyed out.
   */
  canEdit?: boolean;
  /**
   * Which surface the contacts item opens: the contacts list or the gift links (manual sends).
   * `null` (a Universal Link, one shared link) has neither, so the item does not render.
   */
  contactsMode?: ContactsMenuMode | null;
  /**
   * Only an automated campaign has a card of its own, so the item RENDERS only for those — offering
   * it on a prepaid campaign would be an option with nothing behind it.
   */
  onChangeCard?: () => void;
  /** True once that card has been declining: the item says so rather than reading as housekeeping. */
  cardFailing?: boolean;
  /** Which actions this campaign allows the caller; a role-allowed one that is `false` is greyed. */
  allowedActions: CampaignAllowedActions;
}

const MoreOptionsDropdown = forwardRef<HTMLDivElement, MoreOptionsDropdownProps>(
  (
    {
      onClose,
      onRename,
      onClone,
      onEdit,
      onPreview,
      onManageContacts,
      onDelete,
      onPause,
      onResume,
      onCancel,
      onArchive,
      onRestore,
      onDisable,
      onEnable,
      onCopyLink,
      onRedeemQr,
      onChangeCard,
      cardFailing,
      canPreview = true,
      canPause,
      canResume,
      canDisable,
      canEnable,
      canCancel,
      canArchive,
      canRestore,
      canDelete,
      blockedReason,
      canEdit = true,
      contactsMode = "contacts",
      allowedActions,
    },
    ref,
  ) => {
    // A view-only role gets Edit greyed, Preview and the read-only contacts: every other item sits
    // behind its own PermissionGate, and the "why no terminal action" note is about actions they
    // could never take.
    const { can } = usePermissions();
    const canEditCampaign = can("edit", "CAMPAIGN");

    const blockedTitle = canEditCampaign
      ? CAMPAIGN_ACTION_OUT_OF_SCOPE_TOOLTIP
      : CAMPAIGN_ACTION_VIEW_ONLY_TOOLTIP;
    const inScope = (action: keyof CampaignAllowedActions) => ({
      disabled: !allowedActions[action],
      title: allowedActions[action] ? undefined : blockedTitle,
    });

    function handleOption(handler?: () => void) {
      handler?.();
      onClose();
    }

    return (
      <Box
        ref={ref}
        sx={{
          bgcolor: "white",
          border: "1px solid var(--color-smoke)",
          borderRadius: "10px",
          boxShadow: "0 8px 28px rgba(0,0,0,0.12)",
          p: "6px",
          width: 210,
          display: "flex",
          flexDirection: "column",
          mt: "6px",
        }}
      >
        <PermissionGate action="edit" subject="CAMPAIGN">
          <MenuRow
            icon={EditOutlinedIcon}
            label="Rename"
            onClick={() => handleOption(onRename)}
            {...inScope("edit")}
          />
        </PermissionGate>

        <PermissionGate action="create" subject="CAMPAIGN">
          <MenuRow
            icon={ContentCopyOutlinedIcon}
            label="Clone"
            onClick={() => handleOption(onClone)}
            {...inScope("edit")}
          />
        </PermissionGate>

        {canEdit && (
          <PermissionGate action="view" subject="CAMPAIGN">
            <MenuRow
              icon={TuneOutlinedIcon}
              label="Edit"
              onClick={() => handleOption(onEdit)}
              {...inScope("edit")}
            />
          </PermissionGate>
        )}

        {canPreview && (
          <PermissionGate action="view" subject="CAMPAIGN">
            <MenuRow
              icon={VisibilityOutlinedIcon}
              label="Preview"
              onClick={() => handleOption(onPreview)}
            />
          </PermissionGate>
        )}

        {/* Opens for anyone who sees the campaign; read-only where they may not edit it. The contacts surface
            is null for a Universal Link (one shared link, no per-recipient list), so this is hidden. */}
        {contactsMode && (
          <PermissionGate action="view" subject="CAMPAIGN">
            <MenuRow
              icon={ContactsOutlinedIcon}
              label={contactsMenuLabel(contactsMode, allowedActions.edit)}
              onClick={() => handleOption(onManageContacts)}
            />
          </PermissionGate>
        )}

        {/* Universal Link: the one post-launch distribution action — copy the shared public link. */}
        {onCopyLink && (
          <PermissionGate action="view" subject="CAMPAIGN">
            <MenuRow
              icon={LinkOutlinedIcon}
              label="Copy Universal Link"
              onClick={() => handleOption(onCopyLink)}
            />
          </PermissionGate>
        )}

        {/* gfg campaigns, once Active: the Redeem link & QR modal (the card gates when this is passed). */}
        {onRedeemQr && (
          <PermissionGate action="view" subject="CAMPAIGN">
            <MenuRow
              icon={QrCode2OutlinedIcon}
              label="QR Code"
              onClick={() => handleOption(onRedeemQr)}
            />
          </PermissionGate>
        )}

        {onChangeCard && (
          <PermissionGate action="edit" subject="CAMPAIGN">
            <MenuRow
              icon={CreditCardOutlinedIcon}
              label={cardFailing ? "Fix Payment Card" : "Change Payment Card"}
              onClick={() => handleOption(onChangeCard)}
              {...inScope("edit")}
            />
          </PermissionGate>
        )}

        {canPause && (
          <PermissionGate action="pause" subject="CAMPAIGN">
            <MenuRow
              icon={PauseCircleOutlineIcon}
              label="Pause"
              onClick={() => handleOption(onPause)}
              {...inScope("pause")}
            />
          </PermissionGate>
        )}

        {canResume && (
          <PermissionGate action="resume" subject="CAMPAIGN">
            <MenuRow
              icon={PlayCircleOutlineIcon}
              label="Resume"
              onClick={() => handleOption(onResume)}
              {...inScope("resume")}
            />
          </PermissionGate>
        )}

        {/* Universal Link's pause/resume equivalent: turn the shared /u/ link off/on. */}
        {canDisable && (
          <PermissionGate action="disable" subject="CAMPAIGN">
            <MenuRow
              icon={LinkOffIcon}
              label="Disable"
              onClick={() => handleOption(onDisable)}
              {...inScope("disable")}
            />
          </PermissionGate>
        )}

        {canEnable && (
          <PermissionGate action="enable" subject="CAMPAIGN">
            <MenuRow
              icon={LinkIcon}
              label="Enable"
              onClick={() => handleOption(onEnable)}
              {...inScope("enable")}
            />
          </PermissionGate>
        )}

        {canCancel && (
          <PermissionGate action="cancel" subject="CAMPAIGN">
            <MenuRow
              icon={EventBusyIcon}
              label="Cancel campaign"
              onClick={() => handleOption(onCancel)}
              {...inScope("cancel")}
            />
          </PermissionGate>
        )}

        {canArchive && (
          <PermissionGate action="archive" subject="CAMPAIGN">
            <MenuRow
              icon={Inventory2OutlinedIcon}
              label="Archive"
              onClick={() => handleOption(onArchive)}
              {...inScope("archive")}
            />
          </PermissionGate>
        )}

        {canRestore && (
          <PermissionGate action="restore" subject="CAMPAIGN">
            <MenuRow
              icon={UnarchiveOutlinedIcon}
              label="Restore from archive"
              onClick={() => handleOption(onRestore)}
              {...inScope("restore")}
            />
          </PermissionGate>
        )}

        {canDelete && (
          <PermissionGate action="delete" subject="CAMPAIGN">
            <MenuRow
              icon={DeleteOutlineIcon}
              label="Delete"
              onClick={() => handleOption(onDelete)}
              {...inScope("delete")}
            />
          </PermissionGate>
        )}

        {/* No terminal action applies (e.g. a live campaign) — explain why rather than leave a gap. */}
        {canEditCampaign && blockedReason && (
          <Box
            sx={{
              display: "flex",
              gap: "10px",
              px: "12px",
              py: "9px",
              alignItems: "flex-start",
              mt: "4px",
              borderTop: "1px solid var(--color-smoke)",
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 16, color: "var(--text-dust-ink)", flexShrink: 0 }} />
            <Typography
              sx={{
                fontFamily: "var(--font-lato)",
                fontSize: "12.5px",
                lineHeight: 1.45,
                color: "var(--color-graphite)",
              }}
            >
              {blockedReason}
            </Typography>
          </Box>
        )}
      </Box>
    );
  },
);

MoreOptionsDropdown.displayName = "MoreOptionsDropdown";
export default MoreOptionsDropdown;
